import { Link } from "react-router-dom";
import { AlertCircle, AlertTriangle, ShieldCheck } from "lucide-react";

export function LandingProof() {
  return (
    <section className="py-20 w-full flex items-center justify-center bg-white text-black border-t-2 border-black/10 px-6 md:px-20 md:snap-start">
      {/* Background white, 2px border top, lots of whitespace as per Section 4 specifications */}
      <div className="max-w-6xl py-12 space-y-12 w-full text-center select-none font-mono">
        
        <div className="space-y-3">
          <span className="font-mono text-xs uppercase tracking-widest text-zinc-500 font-bold">[REAL SCENARIOS AS PROOF]</span>
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-black">
            Incident Scenarios We Solve Instantly
          </h2>
          <p className="text-xs uppercase text-zinc-500 max-w-lg mx-auto font-black leading-relaxed">
            Instead of fake logos, here are the real-life debugging problems we prevent.
          </p>
        </div>
        
        {/* 3-Column use-case grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-6 text-left">
          
          {/* Card 1: Scenario A */}
          <div className="border-4 border-black p-6 space-y-4 bg-white shadow-[6px_6px_0_0_#000] text-xs flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-zinc-200 pb-2">
                <span className="font-bold text-red-500">2:47 AM</span>
                <span className="text-zinc-400 font-bold flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> CRITICAL</span>
              </div>
              <p className="font-black text-black uppercase">Sentry: 124 Exception Errors</p>
              
              <div className="space-y-2 pt-2 text-[11px] text-zinc-550 leading-relaxed uppercase">
                <p className="font-bold text-zinc-800">Old way:</p>
                <p>Open laptop. Open Vercel. Open Sentry. Check Git blame history. Spend 43 minutes matching commit diff parameters. Cry 😭.</p>
              </div>

              <div className="space-y-2 pt-2 border-t border-zinc-100 text-[11px] text-black uppercase">
                <p className="text-blue-500 font-bold">With UptimeWing:</p>
                <p className="font-black">Slack: "Deploy 241 broke auth connection pool. Rollback." Fixed in 30 seconds.</p>
              </div>
            </div>
            <div className="border-t border-black/10 pt-4 mt-4 text-[10px] text-zinc-400 font-black uppercase">
              ⏳ Outage fix time: 30s
            </div>
          </div>

          {/* Card 2: Scenario B */}
          <div className="border-4 border-black p-6 space-y-4 bg-white shadow-[6px_6px_0_0_#000] text-xs flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-zinc-200 pb-2">
                <span className="font-bold text-yellow-500">5:32 PM</span>
                <span className="text-zinc-400 font-bold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> METRICS SPIKE</span>
              </div>
              <p className="font-black text-black uppercase">Datadog: DB Memory Usage Spike 98%</p>
              
              <div className="space-y-2 pt-2 text-[11px] text-zinc-550 leading-relaxed uppercase">
                <p className="font-bold text-zinc-800">Old way:</p>
                <p>Slack panic channel starts. Log trace searches. CPU thread audits. You are the CEO and developer, trying to guess what queries did this.</p>
              </div>

              <div className="space-y-2 pt-2 border-t border-zinc-100 text-[11px] text-black uppercase">
                <p className="text-blue-500 font-bold">With UptimeWing:</p>
                <p className="font-black">Slack alert: "Deploy #882bcf triggered Sequelize pool exhaustion. Run: pm2 restart app."</p>
              </div>
            </div>
            <div className="border-t border-black/10 pt-4 mt-4 text-[10px] text-zinc-400 font-black uppercase">
              ⏳ Outage fix time: 10s
            </div>
          </div>

          {/* Card 3: Scenario C */}
          <div className="border-4 border-black p-6 space-y-4 bg-white shadow-[6px_6px_0_0_#000] text-xs flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-zinc-200 pb-2">
                <span className="font-bold text-green-500">1:15 PM</span>
                <span className="text-zinc-400 font-bold flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> REVENUE WARNING</span>
              </div>
              <p className="font-black text-black uppercase">Stripe: Failed Charges Spiking</p>
              
              <div className="space-y-2 pt-2 text-[11px] text-zinc-550 leading-relaxed uppercase">
                <p className="font-bold text-zinc-800">Old way:</p>
                <p>Support tickets come in. Stripe Dashboard checking. Stripe API status is up. You deploy a quick hotfix that breaks other API endpoints.</p>
              </div>

              <div className="space-y-2 pt-2 border-t border-zinc-100 text-[11px] text-black uppercase">
                <p className="text-blue-500 font-bold">With UptimeWing:</p>
                <p className="font-black">Slack alert: "Stripe connection refused due to failed client SSL handshake variables. Fix: renew certs."</p>
              </div>
            </div>
            <div className="border-t border-black/10 pt-4 mt-4 text-[10px] text-zinc-400 font-black uppercase">
              ⏳ Outage fix time: 15s
            </div>
          </div>

        </div>

        <div className="pt-8">
          <Link to="/signup" className="font-mono text-xs font-black uppercase tracking-wider text-blue-500 hover:underline">
            [See how it works in your stack →]
          </Link>
        </div>
      </div>
    </section>
  );
}
